import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ROLE_LABELS } from "@/lib/permissions";
import SearchInput from "@/components/SearchInput";

const ROLE_OPTIONS = ["front_office", "cashier", "production", "releasing", "accounting", "super_admin"];

const empty = { email: "", name: "", phone: "", role: "front_office", password: "", active: true };

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...empty, ...u, password: "" }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, phone: form.phone, role: form.role, active: form.active };
        if (form.password) payload.password = form.password;
        await api.patch(`/admin/users/${editing.user_id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/admin/users", form);
        toast.success("User created");
      }
      setOpen(false);
      fetchUsers();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save");
    } finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("User deleted");
      fetchUsers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const filtered = users.filter((u) =>
    !search || u.email.toLowerCase().includes(search.toLowerCase()) || u.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 lg:p-10 pav-fade-in" data-testid="admin-users-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Team</p>
          <h1 className="font-display text-3xl font-extrabold text-[#0B1E3F] mt-1">User management</h1>
          <p className="text-sm text-neutral-500 mt-1">Create and manage staff accounts and roles.</p>
        </div>
        <button onClick={openCreate} className="btn-accent inline-flex items-center gap-2" data-testid="user-create-button">
          <Plus className="h-4 w-4" /> Add user
        </button>
      </div>

      <div className="pav-card p-0 overflow-hidden">
        <div className="p-4 border-b border-[#E2E8F0]">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search by name or email…"
            data-testid="user-search-input" />
        </div>
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F4F6FB] text-xs uppercase tracking-wider text-neutral-600">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Role</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0] text-sm">
                {filtered.map((u) => (
                  <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                    <td className="px-4 py-3 font-semibold">{u.name}</td>
                    <td className="px-4 py-3 text-neutral-700">{u.email}</td>
                    <td className="px-4 py-3"><span className="pav-chip">{ROLE_LABELS[u.role] || u.role}</span></td>
                    <td className="px-4 py-3">
                      <span className={`pav-chip ${u.active === false ? "status-cancelled" : "status-completed"}`}>
                        {u.active === false ? "inactive" : "active"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEdit(u)} data-testid={`user-edit-${u.user_id}`} className="p-1.5 hover:bg-[#F4F6FB] rounded mr-1">
                        <Pencil className="h-4 w-4 text-[#0B1E3F]" />
                      </button>
                      {u.role !== "customer" && (
                        <button onClick={() => remove(u)} data-testid={`user-delete-${u.user_id}`} className="p-1.5 hover:bg-red-50 rounded">
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan="5" className="px-4 py-10 text-center text-neutral-500">No users found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit user" : "Add user"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Full name</label>
              <input className="pav-input mt-1" data-testid="user-name-input"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Email</label>
              <input className="pav-input mt-1" type="email" disabled={!!editing}
                data-testid="user-email-input"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Phone</label>
              <input className="pav-input mt-1"
                data-testid="user-phone-input"
                value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Role</label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="pav-input mt-1" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">{editing ? "New password (leave blank to keep)" : "Password"}</label>
              <input className="pav-input mt-1" type="password" minLength={editing ? 0 : 6}
                data-testid="user-password-input"
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="user-active-checkbox"
                checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              Active
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-outline text-sm" data-testid="user-cancel-button">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="user-save-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
